import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { salesTools, runSalesTool, salesOutputs } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Briefcase, Copy, Sparkle, ClockCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

export const SalesToolkit = () => {
  const { activeId } = useClient();
  const qc = useQueryClient();
  const [tool, setTool] = useState("proposal");
  const [inputs, setInputs] = useState({});
  const [output, setOutput] = useState(null);
  const { data: tools = [] } = useQuery({ queryKey: ["sales-tools"], queryFn: salesTools, staleTime: Infinity });
  const { data: history = [] } = useQuery({ queryKey: ["sales-outputs", activeId], queryFn: () => salesOutputs(activeId), enabled: !!activeId });
  const active = tools.find((t) => t.key === tool);
  const run = useMutation({
    mutationFn: () => runSalesTool({ client_id: activeId, tool, inputs }),
    onSuccess: (r) => { setOutput(r); qc.invalidateQueries({ queryKey: ["sales-outputs", activeId] }); toast.success(`${r.tool_label} generated`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Generation failed"),
  });
  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); };

  return (
    <div className="card-matte p-5 mt-5" data-testid="sales-toolkit">
      <div className="flex items-center gap-2 mb-1">
        <Briefcase size={16} className="brand-text" weight="fill" />
        <span className="font-head font-semibold">Sales Toolkit</span>
      </div>
      <p className="text-xs text-[#9a9a9a] mb-4">Proposals, scripts, contracts, ROI math and more — generated in your brand voice, ready to send.</p>
      <div className="flex flex-wrap gap-1.5 mb-4" data-testid="sales-tool-tabs">
        {tools.map((t) => (
          <button key={t.key} data-testid={`sales-tool-${t.key}`} onClick={() => { setTool(t.key); setOutput(null); setInputs({}); }}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${tool === t.key ? "bg-[#6B3DFF] border-[#6B3DFF] text-white font-semibold" : "border-white/12 text-[#9a9a9a] hover:text-white"}`}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="space-y-3">
          {(active?.fields || []).map((f) => (
            <div key={f.key}>
              <div className="label-caps mb-1">{f.label}</div>
              <Textarea data-testid={`sales-input-${f.key}`} value={inputs[f.key] || ""} rows={2}
                onChange={(e) => setInputs((d) => ({ ...d, [f.key]: e.target.value }))}
                placeholder={`${f.label}…`} className="bg-[#10101A] border-white/10 text-sm" />
            </div>
          ))}
          <Button data-testid="sales-tool-generate" onClick={() => run.mutate()} disabled={run.isPending || !activeId}
            className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white rounded-full h-10 px-5 w-full">
            <Sparkle size={15} className="mr-1.5" weight="fill" /> {run.isPending ? "Generating…" : `Generate ${active?.label || ""}`}
          </Button>
          {history.length > 0 && (
            <div>
              <div className="label-caps mb-1.5 flex items-center gap-1"><ClockCounterClockwise size={11} /> Recent</div>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {history.slice(0, 6).map((h) => (
                  <button key={h.id} data-testid={`sales-history-${h.id}`} onClick={() => setOutput(h)}
                    className="w-full text-left text-[11px] text-[#9a9a9a] hover:text-white truncate rounded-lg border border-white/8 px-2.5 py-1.5">
                    <span className="brand-text font-semibold">{h.tool_label}</span> · {(h.created_at || "").slice(0, 16).replace("T", " ")}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="rounded-xl border border-white/8 bg-[#10101A] p-4 min-h-[220px] relative" data-testid="sales-tool-output">
          {output ? (
            <>
              <button data-testid="sales-output-copy" onClick={() => copy(output.output)}
                className="absolute top-3 right-3 flex items-center gap-1 text-[10px] text-[#9a9a9a] hover:text-white border border-white/10 rounded-full px-2.5 py-1">
                <Copy size={11} /> Copy
              </button>
              <div className="label-caps text-[9px] brand-text mb-2">{output.tool_label}</div>
              <pre className="text-xs text-[#e6e6e6] whitespace-pre-wrap font-sans leading-relaxed max-h-96 overflow-y-auto">{output.output}</pre>
            </>
          ) : (
            <div className="h-full flex items-center justify-center text-xs text-[#5a5a5a] text-center px-6">
              {run.isPending ? "Your AI Sales Manager is writing…" : "Fill in the details and generate — output appears here, ready to copy."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
